import { useTranslation } from 'react-i18next';
import { CURRENCIES } from '@/lib/calculationEngine';
import { buildAssumptionRows, formatAssumptionValue } from '@/lib/assumptions';
import { ProvenanceBadge } from '@/components/trust/ProvenanceBadge';
import { ConfidenceIndicator } from '@/components/trust/ConfidenceIndicator';

const KEY_ASSUMPTION_IDS = [
  'consultantRate', 'internalRate', 'deliveryModel', 'effectivePersonnel', 'locations',
];

const symbolOf = (config) => CURRENCIES.find(c => c.value === config?.currency)?.symbol || '€';
const money = (config) => (value) => `${symbolOf(config)}${Number(value || 0).toLocaleString()}`;

function Metric({ label, value, testId, strong = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5" data-testid={testId}>
      <dt className="text-xs text-[#66707A]">{label}</dt>
      <dd
        className={`text-right ${strong ? 'text-base font-semibold text-[#171A1D]' : 'text-sm text-[#171A1D]'}`}
        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
      >
        {value}
      </dd>
    </div>
  );
}

function ScenarioCard({ scenario, index, isSelected, baseline }) {
  const { t } = useTranslation();
  const fmt = money(scenario.config);
  const results = scenario.results;
  const external = (scenario.tco?.years?.[0]?.components || []).find(c => c.id === 'externalConsulting');
  const assumptions = buildAssumptionRows(results.assumptions || [], { t, formatCost: fmt })
    .filter(row => KEY_ASSUMPTION_IDS.includes(row.id));

  const year1Delta = index === 0 ? null : results.totalCost - baseline.results.totalCost;
  const tcoDelta = index === 0 || !scenario.tco || !baseline.tco
    ? null : scenario.tco.total - baseline.tco.total;

  const delta = (value) => value === null ? null : (
    <span className={`ml-2 text-[11px] ${value > 0 ? 'text-[#171A1D]' : 'text-[#527F19]'}`}>
      {value > 0 ? '+' : '−'}{fmt(Math.abs(value))}
    </span>
  );

  return (
    <article
      className={`rounded-lg border bg-white ${isSelected ? 'border-[#527F19]/50 shadow-[0_1px_0_rgba(82,127,25,0.25)]' : 'border-[#DDE1E5]'}`}
      data-testid={`shared-scenario-${index}`}
    >
      <header className="px-5 py-4 border-b border-[#DDE1E5]">
        <div className="flex flex-wrap items-center gap-2">
          {index === 0 && (
            <span
              className="inline-flex items-center border border-[#DDE1E5] bg-[#F5F6F7] px-1.5 py-0.5 rounded-sm text-[10px] font-medium uppercase tracking-[0.08em] text-[#66707A]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              {t('sharedScenarios.baseline')}
            </span>
          )}
          {isSelected && (
            <span
              className="inline-flex items-center border border-[#527F19]/35 bg-[#F1F8E8] px-1.5 py-0.5 rounded-sm text-[10px] font-medium uppercase tracking-[0.08em] text-[#527F19]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid={`shared-scenario-selected-${index}`}
            >
              {t('sharedScenarios.selected')}
            </span>
          )}
        </div>
        <h2 className="mt-2 text-lg font-medium tracking-tight text-[#171A1D] break-words">{scenario.name}</h2>
      </header>

      <div className="px-5 py-4">
        <div className="pb-3 border-b border-[#DDE1E5]">
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#66707A]"
               style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            {t('sharedScenarios.year1')}
          </div>
          <div className="mt-1 text-3xl font-semibold text-[#171A1D]"
               style={{ fontFamily: 'IBM Plex Mono, monospace' }}
               data-testid={`shared-scenario-year1-${index}`}>
            {fmt(results.totalCost)}
            {delta(year1Delta)}
          </div>
          <div className="mt-1 text-xs text-[#66707A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            {t('sharedScenarios.planningRange')}: {fmt(results.costRange.low)} – {fmt(results.costRange.high)}
          </div>
        </div>

        <dl x-excluded="true" className="divide-y divide-[#DDE1E5]">
          {scenario.tco && (
            <Metric
              label={t('sharedScenarios.threeYear')}
              value={<>{fmt(scenario.tco.total)}{delta(tcoDelta)}</>}
              testId={`shared-scenario-tco-${index}`}
              strong
            />
          )}
          <Metric label={t('sharedScenarios.effort')} value={`${results.totalEffort} ${t('results.personDays')}`} />
          <Metric label={t('sharedScenarios.internalEffort')} value={`${results.internalDays} ${t('results.personDays')}`} />
          <Metric label={t('sharedScenarios.externalEffort')} value={`${results.consultantDays} ${t('results.personDays')}`} />
          {external && (
            <Metric label={t('sharedScenarios.externalSpend')} value={fmt(external.amount)} />
          )}
          <Metric label={t('sharedScenarios.timeline')} value={`${results.totalDurationWeeks} ${t('estimates.weeks')}`} />
        </dl>

        {results.confidence && (
          <div className="mt-3">
            <ConfidenceIndicator confidence={results.confidence} compact />
          </div>
        )}

        {assumptions.length > 0 && (
          <div className="mt-4 pt-3 border-t border-[#DDE1E5]">
            <h3 className="text-[11px] uppercase tracking-[0.12em] text-[#66707A] mb-2"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              {t('sharedScenarios.keyAssumptions')}
            </h3>
            <dl x-excluded="true" className="space-y-1.5">
              {assumptions.map(row => (
                <div key={row.id} className="flex flex-wrap items-baseline justify-between gap-2">
                  <dt className="text-xs text-[#3A4249]">{row.label}</dt>
                  <dd className="flex items-center gap-1.5">
                    <span className="text-xs text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {row.value}
                    </span>
                    <ProvenanceBadge provenance={row.provenance} />
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * Read-only decision package. Decision-oriented on purpose: no editing UI, no
 * engine controls — just the figures a board needs, plus their provenance.
 */
export function SharedComparisonView({ data }) {
  const { t } = useTranslation();
  const scenarios = data.scenarios || [];
  const baseline = scenarios[0];
  const selectedIndex = data.decision?.preferred_scenario_index;
  const fmt = money(baseline?.config);
  const columns = scenarios.length >= 3 ? 'lg:grid-cols-3' : 'md:grid-cols-2';

  const formatPoint = (variable, value) => {
    switch (variable.valueType) {
      case 'currency': return fmt(value);
      case 'level': return `${t('methodologyPage.level')} ${value}`;
      case 'infra': return t(`calcData.infra.${value}`);
      case 'approach': return t(`approach.${value}`);
      case 'reuse':
        if (typeof value === 'number') return value > 0 ? `−${value}%` : '0%';
        return value === 'none' ? t('sensitivity.reuseNone') : t('sensitivity.reuseMax');
      default: return String(value);
    }
  };

  return (
    <div className="space-y-6">
      {(data.decision?.context || data.decision?.note || selectedIndex !== null) && (
        <section className="rounded-lg border border-[#DDE1E5] bg-white px-5 py-4" data-testid="shared-decision-context">
          <h2 className="text-sm font-semibold text-[#171A1D]">{t('sharedScenarios.decisionTitle')}</h2>
          {data.decision?.context && (
            <p className="mt-2 text-sm text-[#3A4249] whitespace-pre-line">{data.decision.context}</p>
          )}
          {selectedIndex !== null && selectedIndex !== undefined && scenarios[selectedIndex] && (
            <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-[#171A1D]">
              <span className="inline-flex items-center border border-[#527F19]/35 bg-[#F1F8E8] px-1.5 py-0.5 rounded-sm text-[10px] font-medium uppercase tracking-[0.08em] text-[#527F19]"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                {t('sharedScenarios.selected')}
              </span>
              <span className="font-medium">{scenarios[selectedIndex].name}</span>
              <span className="text-xs text-[#8A929B]">· {t('sharedScenarios.selectedBy')}</span>
            </p>
          )}
          {data.decision?.note && (
            <div className="mt-3 pt-3 border-t border-[#DDE1E5]">
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#66707A]"
                   style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                {t('sharedScenarios.note')}
              </div>
              <p className="mt-1 text-sm text-[#3A4249] whitespace-pre-line">{data.decision.note}</p>
            </div>
          )}
        </section>
      )}

      <div className={`grid gap-4 ${columns}`} data-testid="shared-scenario-grid">
        {scenarios.map((scenario, index) => (
          <ScenarioCard
            key={`${scenario.name}-${index}`}
            scenario={scenario}
            index={index}
            baseline={baseline}
            isSelected={selectedIndex === index}
          />
        ))}
      </div>

      <section className="rounded-lg border border-[#DDE1E5] bg-white px-5 py-4" data-testid="shared-differences">
        <h2 className="text-sm font-semibold text-[#171A1D]">{t('sharedScenarios.differences')}</h2>
        {(data.differences || []).length === 0 ? (
          <p className="mt-2 text-sm text-[#66707A]" data-testid="shared-differences-empty">
            {t('sharedScenarios.differencesEmpty')}
          </p>
        ) : (
          <ul x-excluded="true" className="mt-3 divide-y divide-[#DDE1E5]">
            {data.differences.map(diff => (
              <li key={diff.id} className="py-2.5" data-testid={`shared-difference-${diff.id}`}>
                <div className="text-xs text-[#66707A]">{t(`assumptions.labels.${diff.id}`)}</div>
                <div className="mt-1 grid gap-1.5 sm:grid-cols-3">
                  {diff.values.map((entry, index) => (
                    <div key={`${diff.id}-${index}`} className="flex flex-wrap items-baseline gap-1.5">
                      <span className="text-[11px] text-[#8A929B] truncate max-w-[45%]">
                        {scenarios[index]?.name}
                      </span>
                      <span className="text-sm text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {formatAssumptionValue({ id: diff.id, value: entry.value },
                          { t, formatCost: money(scenarios[index]?.config) })}
                      </span>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {(data.sensitivity_top || []).length > 0 && (
        <section className="rounded-lg border border-[#DDE1E5] bg-white px-5 py-4" data-testid="shared-sensitivity">
          <h2 className="text-sm font-semibold text-[#171A1D]">{t('sharedScenarios.movesMost')}</h2>
          <p className="mt-1 text-xs text-[#66707A]">{t('sharedScenarios.movesMostIntro')}</p>
          <ul x-excluded="true" className="mt-3 divide-y divide-[#DDE1E5]">
            {data.sensitivity_top.map((variable, index) => (
              <li key={variable.id} className="py-3" data-testid={`shared-driver-${variable.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm text-[#171A1D]">
                    <span className="text-[#8A929B] mr-1.5" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {index + 1}.
                    </span>
                    {t(`sensitivity.vars.${variable.id}`)}
                  </span>
                  <span className="text-[11px] text-[#66707A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {t('sharedScenarios.variation')}: {variable.swing}
                  </span>
                </div>
                <div className="mt-1.5 grid gap-1 sm:grid-cols-2">
                  <span className="text-xs text-[#527F19]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {t('sharedScenarios.downward')}: {variable.lowCostDelta
                      ? `−${fmt(Math.abs(variable.lowCostDelta))}` : '—'}
                  </span>
                  <span className="text-xs text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {t('sharedScenarios.upward')}: {variable.highCostDelta
                      ? `+${fmt(variable.highCostDelta)}` : '—'}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-[#8A929B]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {variable.points.map(point => formatPoint(variable, point.value)).join(' · ')}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-lg border border-[#DDE1E5] bg-[#F5F6F7] px-5 py-4" data-testid="shared-notes">
        <h2 className="text-sm font-semibold text-[#171A1D]">{t('sharedScenarios.notesTitle')}</h2>
        <ul x-excluded="true" className="mt-2 space-y-1.5 text-xs text-[#66707A]">
          <li>{t('tco.note')}</li>
          <li data-testid="shared-recert-note">{t('tco.recertTitle')}: {t('tco.recertExcluded')}</li>
          <li data-testid="shared-surveillance-note">{t('tco.surveillanceBodyNote')}</li>
          <li>{t('tco.surveillanceNote')}</li>
        </ul>
        <p className="mt-3 text-[11px] text-[#8A929B]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
          {t('trust.engineVersion')} {data.engine_version} · {t('tco.modelVersion')} {data.tco_model_version}
        </p>
      </section>
    </div>
  );
}
